import { InvestingTemplate } from "@/interfaces/invtestingTypes";
import InvestingLayout from "@/layout/InvestingLayout";
import { getTemplate } from "@/service/investing.service";
import { GetServerSideProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import React, { ReactElement, useRef } from "react";
import styles from "../index.module.scss";
import rfStore from "@/utils/rfStore";
import cn from "classnames";
import { AppDownLoadAddress } from "@/utils/confiigUtils";
import handleDeepLink from "@/utils/handleDeepLink";
import clientDeepLinks from "@/utils/clientDeepLinks";
import { useRouter } from "next/router";
import getDescription from "../getDescription";
import { useTranslation } from "next-i18next";
import SocialsHeader from "@/containers/investing/SocialsHeader";
import GetApp from "@/containers/investing/GetApp";
import parseUrl from "url-parse";
import InvestingTime from "@/containers/investing/InvestingTime";
import useEventListener from "@/hooks/useEventListener";
import Stocks from "@/containers/investing/Stocks";
import ClientBtnGroup from "@/containers/investing/ClientBtnGroup";
import { useTickersRequest } from "@/hooks/useRequest/useInvestingRequest";
import { useTicketMaps } from "@/store/hooks";
import useWxShare from "@/hooks/useWxShare";

interface TemplateImpl extends InvestingTemplate {
  description: string;
}

export default function InvestingView({ info }: { info: TemplateImpl }) {
  const { t } = useTranslation("investing");
  const router = useRouter();
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  const ticketMap = useTicketMaps();
  const { data: _ } = useTickersRequest(
    {
      symbols: info.quoteSymbols || [],
      locale: router.locale || "en",
    },
    ticketMap.size === 0 // 没有数据 得重新拉去数据
  );
  useWxShare(undefined, undefined, {
    title: info.title,
    desc: info.description,
    imgUrl: info.banner,
  });

  const parseTemplateContent = () => {
    try {
      return JSON.parse(info.templateContent);
    } catch (error) {
      return {
        content: "",
        buttons: [],
      };
    }
  };

  const showMultipleButton = () => {
    return (
      parseTemplateContent().buttons &&
      parseTemplateContent().buttons.length > 1
    );
  };

  const getBtnClassName = (index: number) => {
    if (index === 0) {
      return cn(
        styles.bgPurple,
        showMultipleButton() && styles.deficiencyRight
      );
    }

    return cn(styles.bgBlue, styles.deficiencyLeft);
  };

  const isInApp = () => {
    return rfStore.getRFState()?.model;
  };

  const handleHome = () => {
    if (typeof window === "undefined") {
      return;
    }
    handleDeepLink(clientDeepLinks.assetPage());

    directToDownload("?target=asset");
  };

  const directToDownload = (query: string) => {
    const now = Date.now();
    setTimeout(function () {
      if (Date.now() - now > 100) return;
      window.location.href = AppDownLoadAddress.HOME_PAGE_DOWNLOAD(
        undefined,
        query
      );
    }, 25);
  };

  const handleHalaTime = () => {
    if (typeof window === "undefined") return;
    const historyLen = window.history.length;
    if (historyLen <= 2) {
      router.replace("/investing/hala");
      return;
    }
    router.back();
  };

  const contentBtnClick = (link?: string) => {
    if (!link) return;
    const { query } = parseUrl(link);
    // TODO: 缺少链接约定，判断是否deepLink链接
    if (isInApp()) {
      handleDeepLink(clientDeepLinks.custom(query));
      return;
    }

    window.location.href = link;
  };

  useEventListener(
    "click",
    (e) => {
      const targetEl = e.target as HTMLElement;
      if (!targetEl) return;

      const { market, symbol } = getTargetDomAttrs(targetEl);
      if (!symbol || !market) return;

      if (isInApp()) {
        handleDeepLink(clientDeepLinks.stockDetail(market, symbol));
        return;
      }
      directToDownload(`?target=stockDetail&market=${market}&symbol=${symbol}`);
    },
    contentWrapperRef
  );

  const getTargetDomAttrs = (dom: HTMLElement) => {
    let attrValue = { market: "", symbol: "", name: "", logo: "" };
    const currentDomHasSymbol = domHasSymbolAttr(dom);

    if (currentDomHasSymbol) {
      return getValueFromAttr(dom);
    }

    // 看父节点
    const parentNode = dom.parentNode as HTMLElement;
    if (!parentNode) return attrValue;
    const parentHasSymbol = domHasSymbolAttr(parentNode);
    if (parentHasSymbol) {
      return getValueFromAttr(parentNode);
    }

    return attrValue;
  };

  // 节点是否含有symbol
  const domHasSymbolAttr = (dom: HTMLElement) => {
    return dom.getAttribute("data-symbol") !== null;
  };

  // 获取需要的属性
  const getValueFromAttr = (dom: HTMLElement) => {
    return {
      market: dom.getAttribute("data-market"),
      symbol: dom.getAttribute("data-symbol"),
      name: dom.getAttribute("data-name"),
      logo: dom.getAttribute("data-logo"),
    };
  };

  // 筛选symbol只要stock
  const getStockSymbols = (symbols: string[]) => {
    return symbols.filter((item) => item.length <= 6);
  };

  return (
    <>
      <Head>
        <meta property="og:type" content="website" />
        <meta property="og:title" content={info.title} />
        <meta property="og:description" content={info.description} />
        <meta property="og:image" content={info.banner} />
        <meta property="og:image:width" content="600" />
        <meta property="og:image:height" content="600" />

        <meta name="twitter:title" content={info.title} />
        <meta name="twitter:description" content={info.description} />
        <meta name="twitter:image" content={info.banner} />
        <meta name="twitter:card" content="summary_large_image"></meta>
        <meta name="telegram:channel" content="@telegram" />
        <title>{info.title || t("trading-sparks")}</title>
      </Head>

      <div className={styles.templateContainer}>
        <SocialsHeader onLogoClick={handleHome} />

        <section className={styles.tempateWrapper}>
          <h1 className={styles.title}>{info.title}</h1>
          <div className={styles.updateTime}>
            <InvestingTime date={info.createTime} />
          </div>
          <Stocks
            style={{ marginTop: "0.5rem" }}
            showMarket={true}
            stocks={getStockSymbols(info.quoteSymbols || []).map((item) => {
              return {
                stockName: ticketMap.get(item)?.name || "",
                symbol: item,
                market: ticketMap.get(item)?.market || "",
              };
            })}
          />
          {info.banner && (
            <div className={styles.poster}>
              <img
                src={info.banner + "?x-oss-process=image/resize,w_640"}
                alt="banner"
              />
            </div>
          )}

          <div className={cn(styles.templates, "ql-editor")}>
            <div
              ref={contentWrapperRef}
              dangerouslySetInnerHTML={{
                __html: parseTemplateContent().content,
              }}
            ></div>
          </div>

          {!isInApp() && (
            <div className={styles.buttonSeries}>
              {parseTemplateContent().buttons?.map((btn: any, key: any) => {
                return (
                  <a
                    key={key}
                    className={getBtnClassName(key)}
                    onClick={(e) => {
                      e.preventDefault();
                      contentBtnClick(btn.link);
                    }}
                  >
                    {btn.text}
                  </a>
                );
              })}

              {showMultipleButton() && (
                <span className={styles.deficiencyDevide}></span>
              )}
            </div>
          )}
        </section>

        {!isInApp() ? (
          <GetApp />
        ) : (
          <ClientBtnGroup
            btnS={parseTemplateContent().buttons || []}
            onBtnClick={contentBtnClick}
          />
        )}
      </div>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  let info: InvestingTemplate = {
    templateId: "",
    templateName: "",
    templateType: "",
    title: "",
    banner: "",
    description: "",
    templateContent: "",
    locale: "",
    createTime: "",
    updateTime: "",
    quoteSymbols: [],
    trendLabel: 0,
  };

  let isError = false;
  const templateId = context.params?.id;
  try {
    const data = await getTemplate(
      String(templateId),
      context.locale as string
    );
    if (!data) {
      throw Error("No Response Data");
    }
    info = data;
  } catch (error) {
    console.log(error);
    isError = true;
  }

  return {
    props: {
      ...(await serverSideTranslations(context.locale as string, [
        "investing",
        "common",
      ])),
      info: {
        ...info,
        templateId: info?.templateId.toString(),
        description:
          info?.description || getDescription(info?.templateContent || ""),
      },
    },
    notFound: isError,
  };
};

InvestingView.getLayout = function getLayout(page: ReactElement) {
  return <InvestingLayout>{page}</InvestingLayout>;
};
